import { useEffect, useState } from 'react';
import { FolderTree, Clock } from 'lucide-react';

interface Volume {
  id: string;
  name: string;
  pool: string;
  type: 'filesystem' | 'volume' | 'snapshot';
  mountpoint: string | null;
  usedBytes: number;
  availableBytes: number;
  compression: string;
  readonly: boolean;
  createdAt: string;
}

const formatBytes = (bytes: number) => {
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let i = 0;
  let value = bytes;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  return `${value.toFixed(2)} ${units[i]}`;
};

export default function Volumes() {
  const [volumes, setVolumes] = useState<Volume[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/v1/volumes', { credentials: 'include' })
      .then((r) => r.json())
      .then((res) => setVolumes(res.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-center text-slate-500">Loading volumes...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Volumes & Datasets</h1>
        <button className="px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium rounded-md transition-colors">
          Create Dataset
        </button>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900 overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Pool</th>
              <th className="px-4 py-3 font-medium">Type</th>
              <th className="px-4 py-3 font-medium">Used</th>
              <th className="px-4 py-3 font-medium">Available</th>
              <th className="px-4 py-3 font-medium">Compression</th>
              <th className="px-4 py-3 font-medium">Readonly</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {volumes.map((v) => (
              <tr key={v.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <td className="px-4 py-3 font-medium text-slate-900 dark:text-white flex items-center gap-2">
                  {v.type === 'snapshot' ? <Clock size={16} /> : <FolderTree size={16} />}
                  {v.name}
                </td>
                <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{v.pool}</td>
                <td className="px-4 py-3 capitalize text-slate-600 dark:text-slate-400">{v.type}</td>
                <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{formatBytes(v.usedBytes)}</td>
                <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{formatBytes(v.availableBytes)}</td>
                <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{v.compression}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full ${v.readonly ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    {v.readonly ? 'Yes' : 'No'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
